import React, { useState } from 'react';
import { X, AlertTriangle, Clock, Ban } from 'lucide-react';
import { toast } from 'react-hot-toast';

const BlockVehicleModal = ({ isOpen, onClose, vehicle, onBlock }) => {
  const [blockType, setBlockType] = useState('temporary');
  const [blockReason, setBlockReason] = useState('');
  const [customReason, setCustomReason] = useState('');
  const [blockDuration, setBlockDuration] = useState(24); // hours
  const [isLoading, setIsLoading] = useState(false);

  const predefinedReasons = [
    'Documentation issues',
    'Insurance expired',
    'Fitness certificate expired',
    'PUC certificate expired',
    'Permit expired',
    'Vehicle maintenance issues',
    'Safety concerns',
    'Compliance violations',
    'Other'
  ];

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!blockReason && !customReason) {
      toast.error('Please select or enter a reason for blocking');
      return;
    }

    setIsLoading(true);
    
    try {
      const finalReason = blockReason === 'Other' ? customReason : blockReason;
      const blockExpiry = blockType === 'temporary' 
        ? Date.now() + blockDuration * 60 * 60 * 1000
        : null;

      await onBlock({
        vehicleId: vehicle._id,
        isBlocked: true,
        blockReason: finalReason,
        blockExpiry,
        blockType
      });

      toast.success(`Vehicle ${blockType === 'permanent' ? 'permanently blocked' : 'temporarily blocked'}`);
      onClose();
    } catch (error) {
      toast.error('Failed to block vehicle');
      console.error('Block vehicle error:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const handleUnblock = async () => {
    setIsLoading(true);
    
    try {
      await onBlock({
        vehicleId: vehicle._id,
        isBlocked: false,
        blockReason: null,
        blockExpiry: null,
        blockType: null
      });

      toast.success('Vehicle unblocked successfully');
      onClose();
    } catch (error) {
      toast.error('Failed to unblock vehicle');
      console.error('Unblock vehicle error:', error);
    } finally {
      setIsLoading(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg p-6 w-full max-w-md mx-4">
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-xl font-semibold text-gray-900">
            {vehicle?.isBlocked ? 'Unblock Vehicle' : 'Block Vehicle'}
          </h2>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600"
          >
            <X className="h-5 w-5" />
          </button>
        </div>

        {vehicle?.isBlocked ? (
          // Unblock section
          <div className="space-y-4">
            <div className="flex items-center p-3 bg-yellow-50 rounded-lg">
              <AlertTriangle className="h-5 w-5 text-yellow-600 mr-2" />
              <span className="text-sm text-yellow-800">
                Vehicle is currently blocked
              </span>
            </div>
            
            {vehicle.blockReason && (
              <div className="p-3 bg-gray-50 rounded-lg">
                <p className="text-sm text-gray-600">
                  <strong>Reason:</strong> {vehicle.blockReason}
                </p>
                {vehicle.blockExpiry && (
                  <p className="text-sm text-gray-600 mt-1">
                    <strong>Expires:</strong> {new Date(vehicle.blockExpiry).toLocaleString()}
                  </p>
                )}
              </div>
            )}

            <button
              onClick={handleUnblock}
              disabled={isLoading}
              className="w-full bg-green-600 text-white py-2 px-4 rounded-lg hover:bg-green-700 disabled:opacity-50"
            >
              {isLoading ? 'Unblocking...' : 'Unblock Vehicle'}
            </button>
          </div>
        ) : (
          // Block section
          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="space-y-3">
              <label className="block text-sm font-medium text-gray-700">
                Block Type
              </label>
              <div className="flex space-x-3">
                <label className="flex items-center">
                  <input
                    type="radio"
                    value="temporary"
                    checked={blockType === 'temporary'}
                    onChange={(e) => setBlockType(e.target.value)}
                    className="mr-2"
                  />
                  <Clock className="h-4 w-4 text-blue-600 mr-1" />
                  Temporary
                </label>
                <label className="flex items-center">
                  <input
                    type="radio"
                    value="permanent"
                    checked={blockType === 'permanent'}
                    onChange={(e) => setBlockType(e.target.value)}
                    className="mr-2"
                  />
                  <Ban className="h-4 w-4 text-red-600 mr-1" />
                  Permanent
                </label>
              </div>
            </div>

            {blockType === 'temporary' && (
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Block Duration (hours)
                </label>
                <select
                  value={blockDuration}
                  onChange={(e) => setBlockDuration(parseInt(e.target.value))}
                  className="w-full border border-gray-300 rounded-lg px-3 py-2"
                >
                  <option value={1}>1 hour</option>
                  <option value={6}>6 hours</option>
                  <option value={12}>12 hours</option>
                  <option value={24}>24 hours</option>
                  <option value={48}>48 hours</option>
                  <option value={72}>72 hours</option>
                  <option value={168}>1 week</option>
                </select>
              </div>
            )}

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Reason for Blocking
              </label>
              <select
                value={blockReason}
                onChange={(e) => setBlockReason(e.target.value)}
                className="w-full border border-gray-300 rounded-lg px-3 py-2"
              >
                <option value="">Select a reason</option>
                {predefinedReasons.map((reason) => (
                  <option key={reason} value={reason}>
                    {reason}
                  </option>
                ))}
              </select>
            </div>

            {blockReason === 'Other' && (
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Custom Reason
                </label>
                <textarea
                  value={customReason}
                  onChange={(e) => setCustomReason(e.target.value)}
                  placeholder="Enter custom reason..."
                  className="w-full border border-gray-300 rounded-lg px-3 py-2 h-20 resize-none"
                />
              </div>
            )}

            <div className="flex space-x-3 pt-4">
              <button
                type="button"
                onClick={onClose}
                className="flex-1 bg-gray-300 text-gray-700 py-2 px-4 rounded-lg hover:bg-gray-400"
              >
                Cancel
              </button>
              <button
                type="submit"
                disabled={isLoading || (!blockReason && !customReason)}
                className="flex-1 bg-red-600 text-white py-2 px-4 rounded-lg hover:bg-red-700 disabled:opacity-50"
              >
                {isLoading ? 'Blocking...' : `Block Vehicle (${blockType})`}
              </button>
            </div>
          </form>
        )}
      </div>
    </div>
  );
};

export default BlockVehicleModal; 